/**
 * Additive polytemporal auditory field.
 *
 * This engine complements (and never replaces) the existing binaural,
 * isochronic, texture, and subliminal paths. It uses real amplitude
 * modulation on several audible carriers, so the rhythmic envelope also
 * works on speakers. No carrier is ultrasonic.
 */
export const DEFAULT_POLYTEMPORAL_FIELD = Object.freeze({
  enabled: true,
  clockEpoch: 0,
  targetHz: 6,
  level: 0.018,
  slowHz: 0.10,
  breathHz: 1 / 7.5,
  phraseHz: 1.35,
  textureHz: 37,
  modulationDepth: 0.34,
  // Direct acoustic envelope components. Unlike a binaural difference tone,
  // these rhythms are physically present in the waveform and therefore also
  // survive speaker or mono playback. Every envelope is attenuation-only.
  directAmEnabled: true,
  directAmDepth: 0.44,
  harmonicEnabled: true,
  harmonicDepth: 0.16,
  crossFrequencyEnabled: true,
  crossFrequencyHz: 40,
  crossFrequencyDepth: 0.07,
  spatialWidth: 0.55,
  rotationSeconds: 48,
  transientLevel: 0.12,
  landmarkSeconds: 24,
  jitterPercent: 0.035,
  stereoBalance: 0,
  carrierProfile: "balanced",
  pulseClarity: "clear",
  comfort: "balanced",
  movement: "subtle",
  regularity: "organic",
  breathEnabled: true,
  continuousDeepening: true,
  deepeningCycleSeconds: 360,
  descentBias: 0.82,
  // Director, 2026-08-18: "we are targeting deepest trance, and it should
  // shift further we go, recommended frequencies should apply, ie from theta
  // to gamma... aiming for erotic dreamstates." A smooth band arc glides the
  // felt pulse from theta (dreamy, REM-like) up through alpha into beta and
  // low gamma for erotic arousal. Waypoints are [elapsedSeconds, targetHz].
  bandArcEnabled: true,
  bandArc: [
    [0, 6],      // theta — dreamy, REM-like entry
    [120, 6],    // theta — orientation hold
    [360, 8],    // theta -> low alpha
    [840, 10],   // alpha — absorption
    [1800, 14],  // alpha -> beta
    [3600, 20],  // beta — rising arousal
    [7200, 30],  // low gamma — erotic peak
  ],
});

const clamp = (value, minimum, maximum, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(minimum, Math.min(maximum, number)) : fallback;
};

export function normalizePolytemporalField(value = {}) {
  const defaults = DEFAULT_POLYTEMPORAL_FIELD;
  const choice = (key, choices) => choices.includes(String(value[key])) ? String(value[key]) : defaults[key];
  return {
    enabled: value.enabled === undefined ? defaults.enabled : Boolean(value.enabled),
    clockEpoch: Number.isFinite(Number(value.clockEpoch)) ? Number(value.clockEpoch) : defaults.clockEpoch,
    targetHz: clamp(value.targetHz, 0.5, 12, defaults.targetHz),
    level: clamp(value.level, 0, 0.04, defaults.level),
    slowHz: clamp(value.slowHz, 0.08, 0.12, defaults.slowHz),
    breathHz: clamp(value.breathHz, 0.10, 0.25, defaults.breathHz),
    phraseHz: clamp(value.phraseHz, 1, 2, defaults.phraseHz),
    textureHz: clamp(value.textureHz, 30, 45, defaults.textureHz),
    modulationDepth: clamp(value.modulationDepth, 0.08, 0.55, defaults.modulationDepth),
    directAmEnabled: value.directAmEnabled === undefined ? defaults.directAmEnabled : Boolean(value.directAmEnabled),
    directAmDepth: clamp(value.directAmDepth, 0, 0.55, defaults.directAmDepth),
    harmonicEnabled: value.harmonicEnabled === undefined ? defaults.harmonicEnabled : Boolean(value.harmonicEnabled),
    harmonicDepth: clamp(value.harmonicDepth, 0, 0.22, defaults.harmonicDepth),
    crossFrequencyEnabled: value.crossFrequencyEnabled === undefined
      ? defaults.crossFrequencyEnabled : Boolean(value.crossFrequencyEnabled),
    crossFrequencyHz: clamp(value.crossFrequencyHz, 30, 45, defaults.crossFrequencyHz),
    crossFrequencyDepth: clamp(value.crossFrequencyDepth, 0, 0.10, defaults.crossFrequencyDepth),
    spatialWidth: clamp(value.spatialWidth, 0, 1, defaults.spatialWidth),
    rotationSeconds: clamp(value.rotationSeconds, 24, 180, defaults.rotationSeconds),
    transientLevel: clamp(value.transientLevel, 0, 0.24, defaults.transientLevel),
    landmarkSeconds: clamp(value.landmarkSeconds, 12, 60, defaults.landmarkSeconds),
    jitterPercent: clamp(value.jitterPercent, 0.02, 0.05, defaults.jitterPercent),
    stereoBalance: clamp(value.stereoBalance, -0.25, 0.25, defaults.stereoBalance),
    carrierProfile: choice("carrierProfile", ["soft", "balanced", "bright"]),
    pulseClarity: choice("pulseClarity", ["soft", "clear"]),
    comfort: choice("comfort", ["sensitive", "balanced", "strong"]),
    movement: choice("movement", ["still", "subtle", "wide"]),
    regularity: choice("regularity", ["steady", "organic"]),
    breathEnabled: value.breathEnabled === undefined ? defaults.breathEnabled : Boolean(value.breathEnabled),
    continuousDeepening: value.continuousDeepening === undefined
      ? defaults.continuousDeepening : Boolean(value.continuousDeepening),
    deepeningCycleSeconds: clamp(value.deepeningCycleSeconds, 180, 900, defaults.deepeningCycleSeconds),
    descentBias: clamp(value.descentBias, 0, 1, defaults.descentBias),
    bandArcEnabled: value.bandArcEnabled === undefined ? defaults.bandArcEnabled : Boolean(value.bandArcEnabled),
    bandArc: Array.isArray(value.bandArc) && value.bandArc.length >= 2
      ? value.bandArc
        .filter((p) => Array.isArray(p) && p.length === 2 && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1])))
        .map((p) => [Number(p[0]), Number(p[1])])
        .sort((a, b) => a[0] - b[0])
      : defaults.bandArc,
  };
}

/**
 * Auditable description of rhythms physically encoded by the field.
 * This reports stimulus parameters, not a measured or promised brain state.
 */
export function describePolytemporalStimulus(value = {}) {
  const settings = normalizePolytemporalField(value);
  return {
    direct_am_hz: settings.directAmEnabled ? settings.targetHz : 0,
    direct_am_depth: settings.directAmEnabled
      ? settings.modulationDepth * settings.directAmDepth : 0,
    second_harmonic_hz: settings.harmonicEnabled ? settings.targetHz * 2 : 0,
    second_harmonic_depth: settings.harmonicEnabled
      ? settings.modulationDepth * settings.harmonicDepth : 0,
    nested_carrier_hz: settings.crossFrequencyEnabled ? settings.crossFrequencyHz : 0,
    nested_carrier_depth: settings.crossFrequencyEnabled
      ? settings.modulationDepth * settings.crossFrequencyDepth : 0,
    attenuation_only: true,
  };
}

/**
 * Converts a listener's short psychophysical calibration into safe engine
 * controls. These are preference inferences, not medical measurements.
 */
export function calibratePolytemporalProfile(value = {}) {
  const settings = normalizePolytemporalField(value);
  const comfortScale = { sensitive: 0.62, balanced: 1, strong: 1.18 }[settings.comfort];
  const clarityScale = settings.pulseClarity === "soft" ? 0.72 : 1;
  const movementScale = { still: 0.08, subtle: 0.62, wide: 1 }[settings.movement];
  return normalizePolytemporalField({
    ...settings,
    level: settings.level * comfortScale,
    modulationDepth: settings.modulationDepth * clarityScale * comfortScale,
    spatialWidth: settings.spatialWidth * movementScale,
    transientLevel: settings.transientLevel * clarityScale * comfortScale,
    jitterPercent: settings.regularity === "steady" ? 0.02 : settings.jitterPercent,
  });
}

export function resolvePolytemporalStage(elapsedSeconds = 0, value = {}) {
  const elapsed = Math.max(0, Number(elapsedSeconds) || 0);
  const settings = normalizePolytemporalField(value);
  if (elapsed < 120) return { name: "orientation", progress: elapsed / 120, brightness: 1, novelty: 1, width: 1, depth: 0.78 };
  if (elapsed < 360) return { name: "settling", progress: (elapsed - 120) / 240, brightness: 0.82, novelty: 0.72, width: 0.82, depth: 0.92 };
  if (elapsed < 840) return { name: "absorption", progress: (elapsed - 360) / 480, brightness: 0.62, novelty: 0.42, width: 0.58, depth: 1 };
  if (!settings.continuousDeepening) {
    return { name: "stabilization", progress: 1, brightness: 0.48, novelty: 0.22, width: 0.38, depth: 0.62 };
  }
  const progress = ((elapsed - 840) % settings.deepeningCycleSeconds) / settings.deepeningCycleSeconds;
  const wave = 0.5 - 0.5 * Math.cos(progress * Math.PI * 2);
  const bias = settings.descentBias;
  return {
    name: "continuous_deepening",
    progress,
    brightness: 0.48 - 0.12 * wave * bias,
    novelty: 0.18 + 0.06 * (1 - wave),
    width: 0.38 - 0.12 * wave * bias,
    depth: 0.9 + 0.1 * wave * bias,
  };
}

/**
 * Resolve the felt brainwave target at a given elapsed time by interpolating
 * the band arc. Returns the base target when the arc is disabled or empty.
 * The arc glides theta -> alpha -> beta -> low gamma so the pulse deepens
 * toward erotic dreamstates without a jarring step.
 */
export function resolveBandHz(elapsedSeconds = 0, value = {}) {
  const settings = normalizePolytemporalField(value);
  const elapsed = Math.max(0, Number(elapsedSeconds) || 0);
  if (!settings.bandArcEnabled || !Array.isArray(settings.bandArc) || settings.bandArc.length === 0) {
    return settings.targetHz;
  }
  const arc = settings.bandArc;
  if (elapsed <= arc[0][0]) return arc[0][1];
  const last = arc[arc.length - 1];
  if (elapsed >= last[0]) return last[1];
  for (let i = 1; i < arc.length; i += 1) {
    const [t0, hz0] = arc[i - 1];
    const [t1, hz1] = arc[i];
    if (elapsed <= t1) {
      const span = Math.max(1, t1 - t0);
      const t = (elapsed - t0) / span;
      // Smoothstep keeps the glide pleasant (no perceptible corner).
      const s = t * t * (3 - 2 * t);
      return hz0 + (hz1 - hz0) * s;
    }
  }
  return last[1];
}

export function polytemporalSceneSettings(scene = {}) {
  return calibratePolytemporalProfile({
    enabled: scene.polytemporalEnabled !== false,
    clockEpoch: scene.clockEpoch,
    targetHz: scene.polytemporalTargetHz ?? scene.targetBrainwaveHz ?? scene.binHz,
    level: scene.polytemporalLevel,
    slowHz: scene.polytemporalSlowHz,
    breathHz: scene.polytemporalBreathHz
      // Number(x || 3.5) is not NaN-safe: a truthy non-numeric value yields NaN,
      // and Math.max propagates NaN. Coerce each side, then sum.
      ?? (1 / Math.max(5,
        (Number.isFinite(Number(scene.breathInSeconds)) ? Number(scene.breathInSeconds) : 3.5)
        + (Number.isFinite(Number(scene.breathOutSeconds)) ? Number(scene.breathOutSeconds) : 4))),
    phraseHz: scene.polytemporalPhraseHz,
    textureHz: scene.polytemporalTextureHz,
    modulationDepth: scene.polytemporalDepth,
    directAmEnabled: scene.polytemporalDirectAmEnabled !== false,
    directAmDepth: scene.polytemporalDirectAmDepth,
    harmonicEnabled: scene.polytemporalHarmonicEnabled !== false,
    harmonicDepth: scene.polytemporalHarmonicDepth,
    crossFrequencyEnabled: scene.polytemporalCrossFrequencyEnabled !== false,
    crossFrequencyHz: scene.polytemporalCrossFrequencyHz,
    crossFrequencyDepth: scene.polytemporalCrossFrequencyDepth,
    spatialWidth: scene.polytemporalSpatialWidth,
    rotationSeconds: scene.polytemporalRotationSeconds,
    transientLevel: scene.polytemporalTransientLevel,
    landmarkSeconds: scene.polytemporalLandmarkSeconds,
    jitterPercent: scene.polytemporalJitterPercent,
    stereoBalance: scene.polytemporalStereoBalance,
    carrierProfile: scene.polytemporalCarrierProfile,
    pulseClarity: scene.polytemporalPulseClarity,
    comfort: scene.polytemporalComfort,
    movement: scene.polytemporalMovement,
    regularity: scene.polytemporalRegularity,
    breathEnabled: scene.polytemporalBreathEnabled !== false,
    continuousDeepening: scene.polytemporalContinuousDeepening !== false,
    deepeningCycleSeconds: scene.polytemporalDeepeningCycleSeconds,
    descentBias: scene.polytemporalDescentBias,
    bandArcEnabled: scene.polytemporalBandArcEnabled !== false,
    bandArc: scene.polytemporalBandArc,
  });
}

// Self-contained because AudioWorkletGlobalScope cannot import the app bundle.
export const POLYTEMPORAL_WORKLET_SOURCE = `
class PolytemporalAuditoryFieldProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.s = ${JSON.stringify(DEFAULT_POLYTEMPORAL_FIELD)};
    this.sampleIndex = 0;
    this.startedAt = 0;
    this.narrationUntil = 0;
    this.phases = [0, 0, 0, 0, 0, 0, 0];
    this.seed = 0x6d2b79f5;
    this.noiseLow = 0;
    this.nextLandmark = 24;
    this.landmarkEnd = 0;
    this.harmonicPhase = 0;
    this.crossFrequencyPhase = 0;
    this.port.onmessage = ({ data }) => {
      if (data?.type === "settings") this.s = { ...this.s, ...data.settings };
      if (data?.type === "start") {
        this.startedAt = Number(data.at) || currentTime;
        this.nextLandmark = currentTime + this.s.landmarkSeconds;
      }
      if (data?.type === "narration") this.narrationUntil = Math.max(currentTime, Number(data.until) || currentTime);
    };
  }
  random() {
    let x = this.seed;
    x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
    this.seed = x >>> 0;
    return this.seed / 4294967296;
  }
  stage(t) {
    if (t < 120) return [1, 1, 1, .78];
    if (t < 360) return [.82, .72, .82, .92];
    if (t < 840) return [.62, .42, .58, 1];
    if (!this.s.continuousDeepening) return [.48, .22, .38, .62];
    const cycle = Math.max(180, Math.min(900, this.s.deepeningCycleSeconds || 360));
    const progress = ((t - 840) % cycle) / cycle;
    const wave = .5 - .5 * Math.cos(progress * Math.PI * 2);
    const bias = Math.max(0, Math.min(1, this.s.descentBias ?? .82));
    return [
      .48 - .12 * wave * bias,
      .18 + .06 * (1 - wave),
      .38 - .12 * wave * bias,
      .9 + .1 * wave * bias,
    ];
  }
  bandHz(t) {
    const arc = this.s.bandArc;
    if (!this.s.bandArcEnabled || !Array.isArray(arc) || arc.length === 0) return this.s.targetHz;
    if (t <= arc[0][0]) return arc[0][1];
    const last = arc[arc.length - 1];
    if (t >= last[0]) return last[1];
    for (let i = 1; i < arc.length; i += 1) {
      const t0 = arc[i - 1][0], hz0 = arc[i - 1][1];
      const t1 = arc[i][0], hz1 = arc[i][1];
      if (t <= t1) {
        const span = Math.max(1, t1 - t0);
        const u = (t - t0) / span;
        const s = u * u * (3 - 2 * u);
        return hz0 + (hz1 - hz0) * s;
      }
    }
    return last[1];
  }
  process(inputs, outputs) {
    const output = outputs[0];
    if (!output?.[0]) return true;
    const left = output[0];
    const right = output[1] || output[0];
    const twoPi = Math.PI * 2;
    const sr = sampleRate;
    const s = this.s;
    for (let i = 0; i < left.length; i += 1) {
      const now = currentTime + i / sr;
      const elapsed = Math.max(0, now - this.startedAt);
      const [brightness, novelty, stageWidth, stageDepth] = this.stage(elapsed);
      // Orientation begins at an alpha-like bridge when the requested target
      // is lower, then glides into the scene target across two minutes.
      const orientationHz = Math.max(s.targetHz, 8);
      // Director, 2026-08-18: the felt pulse deepens through the band arc
      // (theta -> alpha -> beta -> low gamma) toward erotic dreamstates.
      const arcHz = this.bandHz(elapsed);
      const effectiveTargetHz = elapsed < 120
        ? s.targetHz + (orientationHz - s.targetHz) * (1 - elapsed / 120)
        : arcHz;
      if (now >= this.nextLandmark) {
        // A phase landmark is a short filtered breath/noise accent plus a
        // coherent carrier phase reset. Timing jitter is strictly 2-5%.
        this.phases[3] = this.phases[4] = 0;
        this.harmonicPhase = 0;
        this.landmarkEnd = now + .12;
        const jitter = Math.max(.02, Math.min(.05, s.jitterPercent));
        this.nextLandmark = now + s.landmarkSeconds * (1 + (this.random() * 2 - 1) * jitter);
      }
      const narrationScale = now < this.narrationUntil ? .48 : 1;
      const slow = .5 + .5 * Math.sin(this.phases[0]);
      const breath = .5 + .5 * Math.sin(this.phases[1] - Math.PI / 2);
      const phrase = .5 + .5 * Math.sin(this.phases[2]);
      const target = .5 + .5 * Math.sin(this.phases[3]);
      const harmonic = .5 + .5 * Math.sin(this.harmonicPhase);
      const crossFrequency = .5 + .5 * Math.sin(this.crossFrequencyPhase);
      const texture = .5 + .5 * Math.sin(this.phases[5]);
      const depth = s.modulationDepth * stageDepth;
      // Nested multiplicative envelopes: slow -> breath -> phrase -> theta,
      // plus a deliberately shallow 30-45 Hz texture.
      const slowEnv = 1 - depth * .12 + slow * depth * .12;
      const breathEnv = s.breathEnabled ? 1 - depth * .22 + breath * depth * .22 : 1;
      const phraseEnv = 1 - depth * .12 + phrase * depth * .12 * narrationScale;
      // A stable fundamental, exact second harmonic, and a shallow fast
      // component nested inside the fundamental crest. These are direct
      // acoustic envelopes—not claims that the listener's EEG must follow.
      const directDepth = s.directAmEnabled ? depth * Math.max(0, Math.min(.55, s.directAmDepth)) : 0;
      const harmonicDepth = s.harmonicEnabled ? depth * Math.max(0, Math.min(.22, s.harmonicDepth)) : 0;
      const nestedDepth = s.crossFrequencyEnabled
        ? depth * Math.max(0, Math.min(.10, s.crossFrequencyDepth)) * target
        : 0;
      const targetEnv = 1 - directDepth + target * directDepth;
      const harmonicEnv = 1 - harmonicDepth + harmonic * harmonicDepth;
      const crossFrequencyEnv = 1 - nestedDepth + crossFrequency * nestedDepth;
      const textureEnv = 1 - depth * .035 + texture * depth * .035 * narrationScale;
      const common = slowEnv * breathEnv * phraseEnv * textureEnv
        * targetEnv * harmonicEnv * crossFrequencyEnv;
      const profile = s.carrierProfile === "soft" ? [1, .55, .15] : s.carrierProfile === "bright" ? [.65, .85, .50] : [1, .72, .28];
      // Director, 2026-08-18: "I want their brain to FEEL this without hearing
      // it. a tangible metal/head effect." The old carriers [132, 360, 1800]
      // put a bright 1800 Hz tone in the conscious hearing band — that was the
      // "screeching." Shift the palette to FELT-not-HEARD:
      //   - a low sub-bass carrier (~55 Hz) that reads as physical vibration
      //     in the chest/head, not a pitched tone
      //   - a mid carrier (~110 Hz) for a tactile "metal/head" resonance
      //   - the bright carrier moved UP to ~9 kHz, above the most sensitive
      //     part of adult hearing, so it is felt as pressure/edge rather than
      //     a screech. The amplitude-modulated envelope (the brainwave pulse)
      //     still rides on all three, so the entrainment is felt, not heard.
      const carriers = [55, 110, 9000 * brightness];
      const rotate = Math.sin(twoPi * elapsed / s.rotationSeconds);
      const width = s.spatialWidth * stageWidth;
      const interauralPhase = rotate * width * .42;
      let tonalLeft = 0;
      let tonalRight = 0;
      for (let c = 0; c < 3; c += 1) {
        this.phases[6 + c] = (this.phases[6 + c] || 0) + twoPi * carriers[c] / sr;
        const spread = interauralPhase * ((c + 1) / 3);
        tonalLeft += Math.sin(this.phases[6 + c] - spread) * profile[c];
        tonalRight += Math.sin(this.phases[6 + c] + spread) * profile[c];
      }
      const white = this.random() * 2 - 1;
      this.noiseLow += .035 * (white - this.noiseLow);
      const noise = this.noiseLow * (.28 + .26 * brightness);
      const landmark = now < this.landmarkEnd ? noise * s.transientLevel * novelty * (1 - (this.landmarkEnd - now) / .12) : 0;
      const signalLeft = tonalLeft * .21 * common + noise * .18 * common + landmark;
      const signalRight = tonalRight * .21 * common + noise * .18 * common + landmark;
      const pan = Math.max(-.92, Math.min(.92, rotate * width + s.stereoBalance));
      const leftGain = Math.sqrt((1 - pan) * .5);
      const rightGain = Math.sqrt((1 + pan) * .5);
      const gain = s.enabled ? s.level : 0;
      left[i] = signalLeft * leftGain * gain;
      right[i] = signalRight * rightGain * gain;
      this.phases[0] += twoPi * s.slowHz / sr;
      this.phases[1] += twoPi * s.breathHz / sr;
      this.phases[2] += twoPi * s.phraseHz / sr;
      this.phases[3] += twoPi * effectiveTargetHz / sr;
      this.phases[4] += twoPi * effectiveTargetHz / sr;
      this.phases[5] += twoPi * s.textureHz / sr;
      this.harmonicPhase += twoPi * (effectiveTargetHz * 2) / sr;
      this.crossFrequencyPhase += twoPi * Math.max(30, Math.min(45, s.crossFrequencyHz || 40)) / sr;
      if (this.harmonicPhase > twoPi) this.harmonicPhase -= twoPi;
      if (this.crossFrequencyPhase > twoPi) this.crossFrequencyPhase -= twoPi;
    }
    return true;
  }
}
registerProcessor("polytemporal-auditory-field", PolytemporalAuditoryFieldProcessor);
`;

const registeredContexts = new WeakMap();

export async function ensurePolytemporalWorklet(context) {
  if (!context?.audioWorklet || typeof AudioWorkletNode === "undefined") return false;
  if (registeredContexts.has(context)) return registeredContexts.get(context);
  const promise = (async () => {
    const blob = new Blob([POLYTEMPORAL_WORKLET_SOURCE], { type: "text/javascript" });
    const url = URL.createObjectURL(blob);
    try {
      await context.audioWorklet.addModule(url);
      return true;
    } finally {
      URL.revokeObjectURL(url);
    }
  })().catch(() => false);
  registeredContexts.set(context, promise);
  return promise;
}

export class PolytemporalAuditoryField {
  constructor(context, destination = context?.destination) {
    this.context = context;
    this.destination = destination;
    this.node = null;
    this.limiter = null;
    this.settings = normalizePolytemporalField();
  }

  async start(value = {}) {
    this.settings = normalizePolytemporalField(value);
    if (this.node) return this.apply(this.settings);
    if (!await ensurePolytemporalWorklet(this.context)) return false;
    this.node = new AudioWorkletNode(this.context, "polytemporal-auditory-field", {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
    this.limiter = this.context.createDynamicsCompressor();
    this.limiter.threshold.value = -18;
    this.limiter.knee.value = 8;
    this.limiter.ratio.value = 12;
    this.limiter.attack.value = 0.004;
    this.limiter.release.value = 0.18;
    this.node.connect(this.limiter);
    this.limiter.connect(this.destination);
    this.node.port.postMessage({ type: "settings", settings: this.settings });
    this.node.port.postMessage({
      type: "start",
      at: Number(this.settings.clockEpoch) || this.context.currentTime,
    });
    return true;
  }

  apply(value = {}) {
    this.settings = normalizePolytemporalField({ ...this.settings, ...value });
    this.node?.port.postMessage({ type: "settings", settings: this.settings });
    return Boolean(this.node);
  }

  setNarrationWindow(seconds = 0) {
    if (!this.node || !this.context) return;
    const duration = Math.max(0, Math.min(180, Number(seconds) || 0));
    this.node.port.postMessage({ type: "narration", until: this.context.currentTime + duration });
  }

  stop() {
    this.apply({ enabled: false });
  }

  destroy() {
    try { this.node?.disconnect(); } catch { }
    try { this.limiter?.disconnect(); } catch { }
    this.node = null;
    this.limiter = null;
  }
}
